// ResolutionIndicator.tsx - dev-tool badge showing the current viewport
// resolution on hover. System-wide toggle (SystemSettings.showResolutionIndicator),
// not a per-user preference - see System Settings > Developer.
import React, { useState, useEffect } from 'react';
import { Box, Tooltip } from '@mui/material';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { referenceDataAPI } from '../services/api';

const ResolutionIndicator: React.FC = () => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const [showIndicator, setShowIndicator] = useState<boolean | null>(null);
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      setShowIndicator(null);
      return;
    }

    const fetchSettings = async () => {
      try {
        const settings = await referenceDataAPI.getSystemSettings();
        setShowIndicator(settings.showResolutionIndicator);
      } catch {
        setShowIndicator(false);
      }
    };

    fetchSettings();

    const handleSettingsUpdated = () => fetchSettings();
    window.addEventListener('system-settings-updated', handleSettingsUpdated);
    return () => {
      window.removeEventListener(
        'system-settings-updated',
        handleSettingsUpdated
      );
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!showIndicator) return null;

  return (
    <Tooltip title={`${screenSize.width} \u00D7 ${screenSize.height}`}>
      <Box
        sx={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          padding: '10px',
          backgroundColor: theme.palette.grey[800],
          color: theme.palette.grey[50],
          borderRadius: '8px',
          border: `2px solid ${theme.palette.grey[50]}`,
          zIndex: 0,
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <AspectRatioIcon sx={{ fontSize: '1.4em' }} />
      </Box>
    </Tooltip>
  );
};

export default ResolutionIndicator;
